import type { IndexDef, LevelCode, ObjectiveState } from '@/api/types/readiness'
import { levelNum, LEVELS } from '@/api/types/readiness'
import { allObjectives } from '@/lib/indexes/registry'

// Effective level for the live view: suggested counts at the suggested level,
// unset counts as 0, confirmed/declared at their level (§3.2, Note 5).
export function liveLevel(state: ObjectiveState | undefined): LevelCode {
  if (!state || state.status === 'unset') return 'L0'
  if (state.status === 'suggested') return state.suggested_level ?? state.level
  return state.level
}

// Reported level for snapshots and the export: a suggestion that nobody has
// confirmed is recorded as the last confirmed level, or L0 (§3.2, §9.1).
export function reportedLevel(state: ObjectiveState | undefined): LevelCode {
  if (!state || state.status === 'unset' || state.status === 'suggested') return 'L0'
  return state.level
}

export function needsConfirmation(state: ObjectiveState | undefined): boolean {
  if (!state) return false
  if (state.status === 'suggested') return true
  return (
    (state.status === 'confirmed' || state.status === 'declared') &&
    !!state.suggested_level &&
    state.suggested_level !== state.level
  )
}

export function computeScore(levels: LevelCode[]): number {
  if (levels.length === 0) return 0
  const sum = levels.reduce((acc, l) => acc + levelNum(l), 0)
  return Math.round((100 * sum) / (3 * levels.length))
}

// ── Bands (Iteration 5 §6.3) ────────────────────────────────────────────────
// Names changed in Iteration 5; thresholds did not. The band is derived from
// the score, never stored. Colours carry over by position from the earlier
// build (band 1 → Beginning … band 4 → Advanced).

export type BandKey = 'beginning' | 'foundational' | 'established' | 'advanced'
export interface Band {
  key: BandKey
  label: string
  token: string
  min: number
  max: number
  // One-line reading of the band (§6.3), and the ladder line (§6.4).
  tooltip: string
  short: string
}

export const BANDS: Band[] = [
  { key: 'beginning', label: 'Beginning', token: 'var(--rd-status-neutral)', min: 0, max: 24, tooltip: 'Most objectives are still at L0. Readiness work has barely begun.', short: 'Most objectives are still at L0.' },
  { key: 'foundational', label: 'Foundational', token: 'var(--rd-status-warn)', min: 25, max: 49, tooltip: 'Most objectives have reached L1. Initial efforts are in place in parts of the organisation.', short: 'Most objectives have reached L1.' },
  { key: 'established', label: 'Established', token: 'var(--rd-status-info)', min: 50, max: 74, tooltip: 'Most objectives have reached L2. Practices are defined and applied across the organisation.', short: 'Most objectives have reached L2.' },
  { key: 'advanced', label: 'Advanced', token: 'var(--rd-status-ok)', min: 75, max: 100, tooltip: 'Most objectives are at L2 or L3. Practices are operational and measured; 100 means every objective is at L3.', short: 'Most objectives are at L2 or L3.' },
]

export function scoreBand(score: number): Band {
  const s = Math.round(score)
  return BANDS.find((b) => s >= b.min && s <= b.max) ?? BANDS[0]
}

export const nextBand = (score: number): Band | null => {
  const i = BANDS.indexOf(scoreBand(score))
  return BANDS[i + 1] ?? null
}

export const levelSum = (levels: LevelCode[]): number => levels.reduce((a, l) => a + levelNum(l), 0)

// Average level across the objectives, one decimal (§6.4).
export const avgLevel = (levels: LevelCode[]): number => (levels.length ? Math.round((10 * levelSum(levels)) / levels.length) / 10 : 0)

// Level moves (on any objectives) needed to reach the next band:
// ceil(next_threshold × N × 3 ÷ 100) − sum. At Advanced: moves to 100.
export function movesToNextBand(levels: LevelCode[]): { moves: number; band: Band | null } {
  const n = levels.length
  const sum = levelSum(levels)
  const score = computeScore(levels)
  const next = nextBand(score)
  if (!next) return { moves: Math.max(0, 3 * n - sum), band: null }
  return { moves: Math.max(0, Math.ceil((next.min * n * 3) / 100) - sum), band: next }
}

export const levelToken = (l: LevelCode) => `var(--rd-level-${l.toLowerCase()})`

export interface OverviewStats {
  score: number
  band: Band
  // Objectives at each level; one with no level is in `unset`, never L0.
  distribution: Record<LevelCode, number>
  // labels: each objective's level, or "Not set" (objective 1 first).
  byDomain: Array<{ code: string; name: string; levels: LevelCode[]; labels: string[]; mean: number }>
  weakest: string[]
  unconfirmed: number
  noRegretsStarted: number
  noRegretsTotal: number
  unset: number
  objectives: number
}

export function overviewStats(index: IndexDef, states: Record<string, ObjectiveState>): OverviewStats {
  const items = allObjectives(index)
  const levels = items.map(({ objective }) => liveLevel(states[objective.code]))
  const isUnset = (code: string) => !states[code] || states[code].status === 'unset'
  const distribution = Object.fromEntries(LEVELS.map((l) => [l, 0])) as Record<LevelCode, number>
  items.forEach(({ objective }, i) => {
    if (!isUnset(objective.code)) distribution[levels[i]] += 1
  })
  const byDomain = [...index.domains]
    .sort((a, b) => a.order - b.order)
    .map((d) => {
      const ls = d.objectives.map((o) => liveLevel(states[o.code]))
      const labels = d.objectives.map((o, i) => (isUnset(o.code) ? 'Not set' : ls[i]))
      const mean = ls.length ? ls.reduce((a, l) => a + levelNum(l), 0) / ls.length : 0
      return { code: d.code, name: d.name, levels: ls, labels, mean }
    })
  const minMean = byDomain.length ? Math.min(...byDomain.map((d) => d.mean)) : 0
  const score = computeScore(levels)
  return {
    score,
    band: scoreBand(score),
    distribution,
    byDomain,
    weakest: byDomain.filter((d) => d.mean === minMean).map((d) => d.name),
    unconfirmed: items.filter(({ objective }) => needsConfirmation(states[objective.code])).length,
    noRegretsStarted: items.filter(
      ({ objective }) => objective.no_regrets && levelNum(liveLevel(states[objective.code])) > 0
    ).length,
    noRegretsTotal: items.filter(({ objective }) => objective.no_regrets).length,
    unset: items.filter(({ objective }) => !states[objective.code] || states[objective.code].status === 'unset').length,
    objectives: items.length,
  }
}

// ── Scale `score_0_10` (Iteration 2 §11) ────────────────────────────────────
// Tier n of m maps to 10 × (n−1)/(m−1), one decimal. Dimension score = mean of
// its scored sub-indices; overall = mean of the dimensions; unscored sub-indices
// are excluded.

export const tierScore = (n: number, m: number): number => (m <= 1 ? 10 : Math.round((100 * (n - 1)) / (m - 1)) / 10)

export function liveScore(state: ObjectiveState | undefined): number | null {
  if (!state || state.status === 'unset') return null
  if (state.status === 'suggested') return state.suggested_score ?? state.score
  return state.score ?? null
}

export function reportedScore(state: ObjectiveState | undefined): number | null {
  if (!state || state.status === 'unset' || state.status === 'suggested') return null
  return state.score ?? null
}

export function needsScoreConfirmation(state: ObjectiveState | undefined): boolean {
  if (!state) return false
  if (state.status === 'suggested') return true
  return (state.status === 'confirmed' || state.status === 'declared') && state.suggested_score != null && state.suggested_score !== state.score
}

export const mean1 = (xs: number[]): number | null => (xs.length ? Math.round((10 * xs.reduce((a, b) => a + b, 0)) / xs.length) / 10 : null)

export function computeIndexScore(index: IndexDef, scoreOf: (code: string) => number | null): { overall: number | null; byDomain: Array<{ code: string; name: string; score: number | null; scored: number; total: number }>; unscored: number } {
  const byDomain = [...index.domains]
    .sort((a, b) => a.order - b.order)
    .map((d) => {
      const scores = d.objectives.map((o) => scoreOf(o.code)).filter((x): x is number => x != null)
      return { code: d.code, name: d.name, score: mean1(scores), scored: scores.length, total: d.objectives.length }
    })
  const dims = byDomain.map((d) => d.score).filter((x): x is number => x != null)
  return { overall: mean1(dims), byDomain, unscored: byDomain.reduce((a, d) => a + d.total - d.scored, 0) }
}

// A 0–10 score mapped onto the 0–100 band scale for the ring and bands.
export const scoreToPct = (s: number | null) => (s == null ? 0 : Math.round(s * 10))
